import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';

const MobileSideMenu = ({
  isOpen,
  onClose,
  navItems,
  activeSection,
  onSectionChange,
  user,
  t
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="md:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-30"
          />

          {/* Menu */}
          <motion.div
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="md:hidden fixed left-0 top-0 bottom-0 w-72 bg-black/95 backdrop-blur-xl border-r border-white/[0.05] z-40 overflow-y-auto"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xs font-medium tracking-[0.2em] text-white/30 uppercase">
                  Navigation
                </h2>
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={onClose}
                  className="p-2 bg-white/[0.02] rounded-md hover:bg-white/[0.03] transition-all duration-200"
                >
                  <X className="w-4 h-4 text-white/50" />
                </motion.button>
              </div>

              <div className="space-y-1">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => onSectionChange(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-md transition-all duration-200 ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-emerald-500/[0.02] to-violet-500/[0.02] border border-white/[0.05]'
                        : 'hover:bg-white/[0.02] active:scale-[0.98]'
                    }`}
                  >
                    <motion.div
                      whileTap={{ scale: 0.9 }}
                      className={`p-2 rounded bg-white/[0.02] ${
                        activeSection === item.id
                          ? item.customStyle ? 'bg-white/[0.05]' : 'bg-white/[0.03]'
                          : ''
                      }`}
                    >
                      <div className={
                        activeSection === item.id
                          ? item.customStyle ? 'text-amber-400/80' : 'text-emerald-400/80'
                          : 'text-white/30'
                      }>
                        {React.cloneElement(item.icon, { className: 'w-4 h-4' })}
                      </div>
                    </motion.div>
                    <div className="text-left flex-1">
                      <div className={`text-sm ${
                        activeSection === item.id ? 'text-white/80' : 'text-white/50'
                      }`}>
                        {item.label}
                      </div>
                      <div className={`text-[10px] mt-0.5 ${
                        activeSection === item.id ? 'text-white/50' : 'text-white/30'
                      }`}>
                        {item.description}
                      </div>
                    </div>
                    {activeSection === item.id && (
                      <Check className="w-4 h-4 text-emerald-400/70" />
                    )}
                  </motion.button>
                ))}
              </div>

              {/* User Info */}
              {user && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 pt-6 border-t border-white/[0.05]"
                >
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt="Profile"
                          className="w-10 h-10 rounded-md border border-white/[0.05]"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-white/[0.02] rounded-md flex items-center justify-center border border-white/[0.05]">
                          <span className="font-mono text-sm text-emerald-400/80">
                            {user.displayName ? user.displayName[0].toUpperCase() : user.email[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 w-1.5 h-1.5 bg-emerald-500/70 rounded-full animate-pulse" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white/70 truncate">
                        {user.displayName || t("User")}
                      </div>
                      <div className="text-xs text-white/30 truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileSideMenu;